import { MagicWand02Icon } from '@langchain/untitled-ui-icons';
import { Switch } from '@mui/joy';

import { produce } from 'immer';
import { Dispatch, FC, SetStateAction, useState } from 'react';

import { Tooltip } from '@/design-system';
import {
  EQuickAdjustment,
  TArtifact,
  TArtifactLengthOptions,
  TReadingLevelOptions,
} from '@/hooks/promptCanvas/types';

import PromptCanvas from './PromptCanvas.tsx';
import QuickActions from './QuickActions';
import VersionManager from './VersionManager.tsx';

type TProps = {
  isMutating: boolean;
  isFirstTokenReceived: boolean;
  handleSubmit: (body: {
    improvement_instruction?: string;
    highlightedRange?: [number, number];
    artifactLength?: TArtifactLengthOptions;
    readingLevel?: TReadingLevelOptions;
    customAction?: string;
  }) => void;
  promptToShow: string;
  artifact: TArtifact;
  viewingArtifactIndex: number;
  isCurrentPrompt: boolean;
  selectedArtifactIndex: number;
  setArtifact: Dispatch<SetStateAction<TArtifact>>;
  setPrompt: (prompt: string) => void;
  setSelectedArtifactIndex: (index: number) => void;
  setViewingArtifactIndex: (index: number) => void;
  showDiff: boolean;
  setShowDiff: (show: boolean) => void;
  handleClose: (updateViewingArtifactIndex?: boolean) => void;
  handleOpenSecrets: () => boolean;
};

const CanvasPanel: FC<TProps> = ({
  isMutating,
  isFirstTokenReceived,
  handleSubmit,
  promptToShow,
  artifact,
  viewingArtifactIndex,
  isCurrentPrompt,
  selectedArtifactIndex,
  setArtifact,
  setPrompt,
  setSelectedArtifactIndex,
  setViewingArtifactIndex,
  showDiff,
  setShowDiff,
  handleClose,
  handleOpenSecrets,
}) => {
  const [quickAdjustmentType, _setQuickAdjustmentType] = useState<
    EQuickAdjustment | undefined
  >();

  const setQuickAdjustmentType: Dispatch<
    SetStateAction<EQuickAdjustment | undefined>
  > = (update) => {
    if (handleOpenSecrets()) return;
    _setQuickAdjustmentType(update);
  };

  const onSubmitSelection = (
    metaprompt: string,
    highlightedRange: [number, number] | null
  ) => {
    if (!highlightedRange) return;

    handleSubmit({
      improvement_instruction: metaprompt,
      highlightedRange,
    });
  };

  const renderTitle = () => {
    return (
      <div className="flex flex-row items-center gap-2 pb-3 ">
        <div className="rounded-md border border-primary p-1">
          <MagicWand02Icon />
        </div>
        <span className="text-lg font-semibold text-primary">
          Prompt Canvas
        </span>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col gap-1 overflow-y-auto overflow-x-hidden bg-secondary pl-6 pr-4 pt-4">
      {renderTitle()}
      <div className="flex w-full flex-row gap-1 pr-1">
        <QuickActions
          quickAdjustmentType={quickAdjustmentType}
          setQuickAdjustmentType={setQuickAdjustmentType}
          onSubmit={handleSubmit}
          disabled={isMutating}
        />
        <div className="ml-auto">
          <Tooltip
            title={showDiff ? 'Stop comparing' : 'Compare with previous'}
          >
            <Switch
              size="sm"
              variant="soft"
              checked={showDiff}
              onChange={() => setShowDiff(!showDiff)}
              disabled={
                artifact.contents.length === 1 ||
                isMutating ||
                viewingArtifactIndex === 0
              }
              startDecorator={<span className="text-sm">Diff</span>}
            />
          </Tooltip>
        </div>
      </div>

      <PromptCanvas
        isMutating={isMutating}
        isFirstTokenReceived={isFirstTokenReceived}
        promptToShow={promptToShow}
        onSubmit={onSubmitSelection}
        handleManualPromptUpdate={(newPrompt) => {
          const currentPrompt = artifact.contents[viewingArtifactIndex].content;
          if (currentPrompt === newPrompt) return;
          // replace the current prompt with the new prompt for viewingArtifactIndex
          setArtifact((prev) =>
            produce(prev, (draft) => {
              draft.contents[viewingArtifactIndex].content = newPrompt;
            })
          );
          // if user is editing
          if (isCurrentPrompt) {
            setPrompt(newPrompt);
          }
        }}
        showDiff={showDiff}
        previousPrompt={
          viewingArtifactIndex > 0
            ? artifact.contents[viewingArtifactIndex - 1].content
            : undefined
        }
      />
      <VersionManager
        isCurrentPrompt={isCurrentPrompt}
        isMutating={isMutating}
        numArtifacts={artifact.contents.length}
        viewingArtifactIndex={viewingArtifactIndex}
        setViewingArtifactIndex={setViewingArtifactIndex}
        handleSetPrompt={() => {
          setPrompt(promptToShow);
          setSelectedArtifactIndex(viewingArtifactIndex);
          handleClose(false);
        }}
        handleGoToCurrent={() => {
          setViewingArtifactIndex(selectedArtifactIndex);
          if (selectedArtifactIndex === 0) {
            setShowDiff(false);
          }
        }}
        setShowDiff={setShowDiff}
      />
    </div>
  );
};

export default CanvasPanel;
